<template>
  <div class="w-100">
    <proa-card class="h-100 d-flex flex-column" no-padding>
      <template v-slot:header>
        <div class="d-flex justify-content-between align-items-center">
          <proa-back-button
            @onClick="onClickBackButton"
          />
          <proa-heading class="group-chat-name" detail>{{ $t('pages.management.sharewindSettings.meetingRoomManagement') }}</proa-heading>
          <div>
            <span class="text-secondary font-weight-w6">{{ $t('pages.indexManagementMeetingRooms.meetingRoomsCount') }}</span>
            <span class="ml-2">{{ managedVideoRoomMasters.length + $t('common.room') }}</span>
          </div>
        </div>
      </template>
      <div class="content-container">
        <div class="table-outer-wrapper">
          <div class="table-inner-scroll-wrapper">
            <table class="table-video-room-masters">
              <thead>
                <tr>
                  <th>
                    <div class="attribute-name">
                      <span>{{ $t('models.videoRoomMasters.attributes.roomName') }}</span>
                    </div>
                  </th>
                  <th>
                    <div class="attribute-name">
                      <span>{{ $t('models.videoRoomMasters.attributes.roomCapacity') }}</span>
                    </div>
                  </th>
                  <th colspan="2">
                    <div class="attribute-name">
                      <span>{{ $t('models.videoRoomMasters.attributes.roomType') }}</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="videoRoomMaster in managedVideoRoomMasters"
                  :key="videoRoomMaster.id"
                >
                  <td :class="{'cafe-room': videoRoomMaster.roomType === 'cafeRoom'}">{{ videoRoomMaster.roomName }}</td>
                  <td>{{ videoRoomMaster.roomCapacity + $t('common.person') }}</td>
                  <td>{{ $t(`models.videoRoomMasters.roomTypes.${videoRoomMaster.roomType}`) }}</td>
                  <td class="edit-column">
                    <proa-button
                      v-if="videoRoomMaster.roomType !== 'cafeRoom'"
                      @click.native="showEditRoomNameModal(videoRoomMaster.id)"
                    >
                      {{ $t('pages.indexManagementMeetingRooms.changeRoomName') }}
                    </proa-button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div class="empty-footer"></div>
    </proa-card>
    <proa-modal
      :modal="editRoomNameVideoRoomMasterId != null"
      :title="$t('pages.indexManagementMeetingRooms.changeRoomName')"
      @close="editRoomNameVideoRoomMasterId = null"
    >
     <proa-input-group :invalidFeedback="roomNameDuplicationError ? $t('models.videoRoomMasters.roomNameDuplicationError') : null">
       <template slot="input">
         <proa-input
           v-model="editRoomName"
           type="text"
           :placeholder="$t('models.videoRoomMasters.attributes.roomName')"
           dark
         />
       </template>
     </proa-input-group>
     <template slot="footer">
       <proa-button
          @click.native="editRoomNameVideoRoomMasterId = null"
          :disabled="changeRoomNameRequesting"
          class="mr-2"
        >
          {{ $t('common.cancel') }}
        </proa-button>
        <proa-button
          @click.native="onClickChangeRoomName"
          :loading="changeRoomNameRequesting"
          :disabled="!editRoomName"
          variant="primary"
        >
          {{ $t('common.change') }}
        </proa-button>
      </template>
    </proa-modal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import ManagedVideoRoomMaster from '@/store/models/admin/ManagedVideoRoomMaster'

export default {
  name: 'IndexManagementMeetingRooms',
  computed: {
    managedVideoRoomMasters: function() {
      return ManagedVideoRoomMaster.query().orderBy(videoRoomMaster => {
        return videoRoomMaster.roomType === 'cafeRoom'
      }, 'desc').get()
    },
    ...mapGetters({
      httpError: 'httpError'
    })
  },
  data() {
    return {
      editRoomName: '',
      editRoomNameVideoRoomMasterId: null,
      changeRoomNameRequesting: false,
      roomNameDuplicationError: false
    }
  },
  methods: {
    onClickBackButton: function() {
      this.$router.push({ name: 'management' })
    },
    async onClickChangeRoomName() {
      this.roomNameDuplicationError = false
      this.changeRoomNameRequesting = true
      await this.apiPutManagedVideoRoomMaster({
        videoRoomMasterId: this.editRoomNameVideoRoomMasterId,
        attributes: {
          roomName: this.editRoomName
        }
      })
      this.changeRoomNameRequesting = false

      if (this.httpError) {
        if (this.httpError?.response?.status === 422) this.roomNameDuplicationError = true
      } else {
        this.editRoomNameVideoRoomMasterId = null
      }
    },
    showEditRoomNameModal: function(id) {
      this.roomNameDuplicationError = false
      this.editRoomName = ManagedVideoRoomMaster.find(id).roomName
      this.editRoomNameVideoRoomMasterId = id
    },
    ...mapActions({
      apiGetManagedVideoRoomMasters: 'entities/managedVideoRoomMasters/index',
      apiPutManagedVideoRoomMaster: 'entities/managedVideoRoomMasters/update'
    })
  },
  created() {
    this.apiGetManagedVideoRoomMasters()
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/variables";

.content-container {
  flex-grow: 1;

  .table-outer-wrapper {
    position: relative;
    height: 100%;
  }

  .table-inner-scroll-wrapper {
    position: absolute;
    max-height: 100%;
    width: 100%;
    overflow-y: auto;
    padding-top: 10px;
  }

  .table-video-room-masters {
    overflow-y: auto;
    height: 100%;
    min-width: 100%;
    white-space: nowrap;

    thead {
      border-bottom: 1px solid $dark-border-color;

      &:after {
        content: "";
        display: block;
        height: 10px;
      }

      th {
        position: sticky;
        top: 0;
        background-color: white;

        &:last-child {
          padding-right: 20px;
        }

        .attribute-name {
          background-color: $light-gray-color;
          background-clip: content-box;
          padding-left: 20px;

          span {
            display: block;
            padding: 7px 5px;
            font-size: 13px;
            font-weight: 300;
            color: $secondary-color;
          }
        }
      }
    }

    tbody tr {
      border-bottom: 1px solid $light-border-color;

      td {
        padding: 20px 0 20px 20px;
        height: 60px;

        &.cafe-room {
          color: $light-text-color;
        }

        &.edit-column {
          text-align: right;
          padding-right: 20px;
        }
      }
    }
  }
}

.empty-footer {
  height: 60px;
  border-top: 1px solid $dark-border-color;
}
</style>
